
import React from "react";
import { 
  Card, 
  CardHeader, 
  CardContent 
} from "@/components/ui/card";
import { Trophy, Medal, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { 
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell
} from "@/components/ui/table";
import { LeaderboardUser } from "./types/karma-types";

interface KarmaLeaderboardProps {
  leaderboard: LeaderboardUser[];
  loading: boolean;
  currentUserId?: string;
}

const KarmaLeaderboard: React.FC<KarmaLeaderboardProps> = ({ 
  leaderboard, 
  loading,
  currentUserId 
}) => {
  // Filter out anonymous users (those without a full_name or username)
  const filteredLeaderboard = leaderboard.filter(user => 
    user.full_name || user.username
  );
  
  // Helper function to get rank badge styling
  const getRankBadge = (rank: number) => {
    switch(rank) {
      case 1:
        return { color: "text-yellow-500", icon: <Trophy className="h-4 w-4" /> };
      case 2:
        return { color: "text-gray-400", icon: <Medal className="h-4 w-4" /> };
      case 3:
        return { color: "text-amber-700", icon: <Medal className="h-4 w-4" /> };
      default:
        return { color: "text-muted-foreground", icon: null };
    }
  };
  
  // Helper function to get initials from name safely
  const getInitials = (name: string | null | undefined): string => {
    if (!name) return '?';
    return name.charAt(0).toUpperCase();
  };
  
  return (
    <Card className="overflow-hidden border shadow-sm">
      <CardHeader className="p-4 bg-muted/30 border-b space-y-0">
        <h3 className="font-medium flex items-center text-base">
          <Users className="h-4 w-4 mr-2 text-blue-500" />
          Karma Leaderboard (Real Points)
          <Badge variant="outline" className="ml-auto">
            Top {filteredLeaderboard.length}
          </Badge>
        </h3>
      </CardHeader>
      
      <CardContent className="p-0">
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        ) : filteredLeaderboard.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground">
            <p>No leaderboard data available</p>
          </div>
        ) : (
          <div className="max-h-[280px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[60px]">Rank</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead className="text-right">Real Points</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLeaderboard.map((user) => {
                  const { color, icon } = getRankBadge(user.rank);
                  const isCurrentUser = currentUserId === user.id;
                  const displayName = user.full_name || user.username || "User";
                  // Display actual karma points from database
                  const realKarmaPoints = user.karma_points || 0;
                  
                  return (
                    <TableRow key={user.id} className={isCurrentUser ? "bg-primary/5" : ""}>
                      <TableCell className="font-medium">
                        <div className={`flex items-center ${color}`}>
                          {icon}
                          <span className="ml-1">{user.rank}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          {user.avatar_url ? (
                            <div 
                              className="h-6 w-6 rounded-full bg-cover bg-center" 
                              style={{ 
                                backgroundImage: `url(${user.avatar_url})` 
                              }}
                            />
                          ) : (
                            <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center">
                              {getInitials(displayName)}
                            </div>
                          )}
                          <span className={isCurrentUser ? "font-medium" : ""}>
                            {displayName}
                          </span>
                          {isCurrentUser && (
                            <Badge variant="secondary" className="ml-1 text-xs">You</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {realKarmaPoints.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default KarmaLeaderboard;
